/**
 * A day ahead, to read (1.5.17 §4) — one component for Today's Look ahead and Planning's calendar: the day's visits
 * as booked so far, in the card layout of Today's list (the time from the Day plan, the visit number and period, the
 * apartment and name, the status with its icon and words, the tags), open positions as "Open", a proposal in pencil
 * as "Waiting for confirmation". "This is a future day": nothing here can be pressed — no Arrived, no Finish, no
 * Call, no More, no Book — only Close and, for the roles that have the schedule, the link to that day on it.
 *
 * It reads what Look ahead reads (the appointments and the slot of the one day); it writes nothing.
 */
import { useEffect, useRef, type RefObject } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AppointmentRow } from '@shared/api-types';
import { isLiveOnDay } from '@shared/visits';
import { formatDayKey, formatDayMedium } from '@/lib/dates';
import { useAppointments, useSlots } from '@/lib/queries';
import { usePositionTimes } from '@/lib/useTimes';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { StatusBadge, appointmentStatusKey } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { visitTags } from '@/pages/Today';

/** On a numbered position that day (the same statuses Today places). */
const ON_POSITION = new Set(['Scheduled', 'On Hold', 'In Progress', 'Completed', 'No Show', 'Unable to Complete']);

/** The day's list, read-only. `day` is a day after today. */
export function DayAheadList({ day, schedule = true }: { day: string; schedule?: boolean }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const appts = useAppointments(day, day);
  const slots = useSlots(day, day);
  const { label, period } = usePositionTimes();
  const loading = appts.isLoading || slots.isLoading;

  // 1.6.1: a record that was moved or called off is history, not a line on the day (shared/visits.ts)
  const rows = (appts.data?.appointments ?? []).filter((a) => a.serviceDateKey === day && isLiveOnDay(a.status));
  const slot = slots.data?.slots.find((s) => s.serviceDateKey === day) ?? null;
  const placed = rows.filter((a) => a.position !== null && ON_POSITION.has(a.status) && a.kind !== 'facility');
  const byPosition = new Map(placed.map((a) => [a.position!, a]));
  const capacity = slot?.baseCapacity ?? 0;
  const hasSurge = !!slot?.surgeApproved || placed.some((a) => a.surge);
  const positionCount = Math.max(slot ? capacity + (hasSurge ? 1 : 0) : 0, ...placed.map((a) => a.position ?? 0));
  const facility = rows.filter((a) => a.kind === 'facility');
  const also = rows.filter((a) => a.kind !== 'facility' && !placed.includes(a));
  // what Look ahead counts against the day ("2 of 5"): the visits on a position — a proposal in pencil is not one yet
  const count = placed.length;

  // the count is said once the day has loaded (a screen reader hears what the sheet now shows)
  const said = useRef<string | null>(null);
  useEffect(() => {
    if (!appts.data || loading || said.current === day) return;
    said.current = day;
    const words = formatDayMedium(day, i18n.language);
    announce(rows.length ? t('day17.said', { day: words, count }) : t('day17.saidNone', { day: words }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appts.data, loading, day]);

  const time = (pos: number | null) => label(day, pos) ?? '—';
  const row = (a: AppointmentRow, pos: number | null) => {
    const status = appointmentStatusKey(a.status, a.confirmation);
    const done = ['completed', 'cancelled', 'noShow', 'unable', 'rescheduled'].includes(status);
    return (
      <li key={a.id} className={`visit${done ? ' is-done' : ''}`} data-testid={`ahead-visit-${a.appointmentId}`} data-status={status}>
        <div className="time tnum">
          {time(pos)}
          <small>
            {pos ? t('today.positionN', { n: pos }) : ''}
            {pos && a.servicePeriod ? ' · ' : ''}
            {t(`period.${period(day, pos, a.servicePeriod)}`, { defaultValue: a.servicePeriod })}
          </small>
        </div>
        <div className="who">
          <div className="apt">
            {a.unit} <span>· {a.tenantName}</span>
          </div>
          <div className="sub">
            <span>{a.kind === 'facility' ? (a.locationDetails ?? t('status.facility')) : a.program === 'TECH' ? t('techBook.title') : t('programs.housekeeping')}</span>
          </div>
        </div>
        <div className="side">
          <div className="badges">
            <StatusBadge status={status} />
            {visitTags(a).map((tag) => (
              <StatusBadge key={tag} status={tag} />
            ))}
          </div>
          {/* where Today's button reads "Waiting for confirmation", here it is only the words */}
          {a.status === 'Proposed' ? <span className="help">{t('today.waitingConfirm')}</span> : null}
        </div>
      </li>
    );
  };
  const openRow = (pos: number) => (
    <li key={`open-${pos}`} className="visit is-open" data-testid={`ahead-open-${pos}`} data-status="open">
      <div className="time tnum">
        {time(pos)}
        <small>{t('today.positionN', { n: pos })}</small>
      </div>
      <div className="who">
        <div className="apt">{t('today.open')}</div>
        <div className="sub">
          <span>{pos > capacity ? t('today.surgePosition') : t('today.openHelp')}</span>
        </div>
      </div>
      <div className="side">{pos > capacity ? <StatusBadge status="surge" /> : null}</div>
    </li>
  );

  return (
    <div className="day-ahead" aria-busy={loading}>
      <div className="warn" data-testid="day-ahead-note">
        <Icon name="info" />
        <div>
          <b>{t('day17.futureNote')}</b>
          <div>{t('day17.futureHelp')}</div>
        </div>
      </div>

      {loading ? (
        <p className="help">{t('app.loading')}</p>
      ) : appts.isError ? (
        <div className="warn take" role="alert">
          <Icon name="alert" />
          <div>
            <b>{t('day17.error')}</b>
            <div className="row">
              <Button size="sm" onClick={() => appts.refetch()} data-testid="day-ahead-retry">
                {t('today.retry')}
              </Button>
            </div>
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty" data-testid="day-ahead-empty">
          <Icon name="calendarEmpty" />
          <b>{t('day17.empty')}</b>
          {!slot ? <span>{t('schedule.noSlot')}</span> : null}
        </div>
      ) : (
        <>
          <p className="help" data-testid="day-ahead-count">
            {t('day17.count', { count })}
          </p>
          {positionCount ? (
            <ul className="day-ahead-list" aria-label={t('day17.visits')} data-testid="day-ahead-list">
              {Array.from({ length: positionCount }, (_, i) => i + 1).map((pos) => {
                const a = byPosition.get(pos);
                return a ? row(a, pos) : openRow(pos);
              })}
            </ul>
          ) : null}
          {facility.length ? (
            <>
              <h3 className="list-h">{t('today.facilities')}</h3>
              <ul className="day-ahead-list" aria-label={t('today.facilities')} data-testid="day-ahead-facility">
                {facility.map((a) => row(a, null))}
              </ul>
            </>
          ) : null}
          {also.length ? (
            <>
              <h3 className="list-h">{t('day17.also')}</h3>
              <ul className="day-ahead-list" aria-label={t('day17.also')} data-testid="day-ahead-also">
                {also.map((a) => row(a, null))}
              </ul>
            </>
          ) : null}
        </>
      )}

      {schedule ? (
        <p className="day-ahead-foot">
          <Link className="btn soft sm" to={`/schedule?day=${day}`} data-testid="day-ahead-schedule">
            {t('day17.openSchedule')}
          </Link>
        </p>
      ) : null}
    </div>
  );
}

/**
 * "See this day's list" on Planning's day panel: a day after today opens the read-only list; today and the days
 * before it are Today's own (the live day, the record of a past day), so there it is a link to that page.
 */
export function DayListButton({ day, today, onOpen }: { day: string; today: string; onOpen: (day: string, el: HTMLElement) => void }) {
  const { t } = useTranslation();
  return day > today ? (
    <Button size="sm" variant="secondary" type="button" aria-haspopup="dialog" onClick={(e) => onOpen(day, e.currentTarget)} data-testid="plan-day-list">
      <Icon name="today" />
      {t('day17.seeList')}
    </Button>
  ) : (
    <Link className="btn secondary sm" to={day === today ? '/housekeeping' : `/housekeeping?day=${day}`} data-testid="plan-day-list">
      <Icon name="today" />
      {t('day17.seeOnToday')}
    </Link>
  );
}

/**
 * The sheet that holds the list: titled with the day in words; focus goes in, and back to the day that opened it.
 * `day` null = closed.
 */
export function DayAheadSheet({ day, onClose, returnFocusTo, schedule }: { day: string | null; onClose: () => void; returnFocusTo?: RefObject<HTMLElement | null>; schedule?: boolean }) {
  const { i18n } = useTranslation();
  return (
    <Sheet open={!!day} onOpenChange={(o) => !o && onClose()}>
      {day ? (
        <SheetContent title={`${formatDayKey(day, i18n.language)}, ${day.slice(0, 4)}`} returnFocusTo={returnFocusTo} data-testid="day-ahead-sheet">
          <DayAheadList key={day} day={day} schedule={schedule} />
        </SheetContent>
      ) : null}
    </Sheet>
  );
}
